import { useCallback, useSyncExternalStore } from "react";

/**
 * Reduce motion — the single source of truth for the animation preference.
 *
 * The preference is `system | on | off` (persisted in
 * `localStorage['anyai-reduce-motion']`, key removed when `system`).
 * Device-local by design — never synced to the account (like theme).
 *
 * Applied via two html classes so an explicit Off truly overrides the OS:
 * - `html.reduce-motion` (preference `on`) — force-reduce regardless of the OS;
 * - `html.allow-motion` (preference `off`) — escapes the
 *   `prefers-reduced-motion` media block in globals.css (its selector is
 *   narrowed to `html:not(.allow-motion)`);
 * - `system` — neither class; the media query governs natively, including
 *   live OS flips, with zero JS.
 *
 * The initial classes are set by the inline boot script in `index.html`
 * before React mounts; this hook keeps them in sync afterwards. Backed by a
 * module-level store via `useSyncExternalStore`, mirroring `useTheme`.
 */
export type MotionPreference = "system" | "on" | "off";

const STORAGE_KEY = "anyai-reduce-motion";
const REDUCE_QUERY = "(prefers-reduced-motion: reduce)";

const listeners = new Set<() => void>();

function readStoredPreference(): MotionPreference {
  if (typeof localStorage === "undefined") return "system";
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === "on" || stored === "off" ? stored : "system";
}

let preference: MotionPreference = readStoredPreference();

function applyPreference(pref: MotionPreference): void {
  if (typeof document === "undefined") return;
  const cl = document.documentElement.classList;
  cl.toggle("reduce-motion", pref === "on");
  cl.toggle("allow-motion", pref === "off");
}

/**
 * Resolved value for JS call sites (scroll `behavior` etc.) that pick at call
 * time. Non-hook on purpose — CSS handles everything declarative; this covers
 * the explicit-`behavior` gaps the CSS cannot reach.
 */
export function isMotionReduced(): boolean {
  if (preference === "on") return true;
  if (preference === "off") return false;
  return typeof matchMedia !== "undefined" && matchMedia(REDUCE_QUERY).matches;
}

function emit(): void {
  for (const listener of listeners) listener();
}

function setPreference(next: MotionPreference): void {
  preference = next;
  if (typeof localStorage !== "undefined") {
    if (next === "system") localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, next);
  }
  applyPreference(next);
  emit();
}

// Re-render hook consumers when the OS flips while on `system` (the CSS media
// query already reacts natively — this is for JS readers like HtmlPreview).
if (typeof matchMedia !== "undefined") {
  matchMedia(REDUCE_QUERY).addEventListener("change", () => {
    if (preference === "system") emit();
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getPreference(): MotionPreference {
  return preference;
}

function getResolvedReduced(): boolean {
  return isMotionReduced();
}

export interface UseReducedMotionResult {
  /** The user's stored choice (`system` defers to the OS setting). */
  motion: MotionPreference;
  setMotion: (pref: MotionPreference) => void;
}

export function useReducedMotion(): UseReducedMotionResult {
  const motion = useSyncExternalStore(subscribe, getPreference, getPreference);
  // Also key renders on the *resolved* value: an OS flip while on `system`
  // emits with the preference unchanged, and useSyncExternalStore bails out
  // on an identical snapshot — this second subscription is what re-renders
  // consumers that read `isMotionReduced()` at render time (e.g. HtmlPreview).
  useSyncExternalStore(subscribe, getResolvedReduced, getResolvedReduced);
  const setMotion = useCallback((next: MotionPreference) => setPreference(next), []);
  return { motion, setMotion };
}
